import { FC, useEffect, useState } from "react";
import { Spinner } from "../../../Spinner/Spinner";
import { downloadPDF } from "../../../../helpers/utils";
import Notification from "../../../notification/Notification";
import { BondRecordData } from "../../FormBondApp.types";
import {
  StyledSpinnerContainer,
  StyledPrice,
  StyledBondPreviewSection,
  StyledButtonsWrapper,
  StyledBondPreviewButton,
  StyledBondPreview,
  StyledDownloadButton,
  StyledBorder,
  StyledPriceLabel,
  StyledPriceValue,
  StyledPriceWrapper,
} from "./BondPreview.styled";

interface BondPreviewProps {
  paymentLink: string;
  isLoading: boolean;
  pdfUrl: string;
  bondRecord?: BondRecordData;
  onSubmit: () => void;
  fetchShippingMethods?: () => Promise<void>;
  fetchShippingOptions?: () => Promise<void>;
}

const BondPreview: FC<BondPreviewProps> = (props): JSX.Element => {
  const { 
    paymentLink, 
    isLoading, 
    pdfUrl, 
    bondRecord, 
    onSubmit, 
    fetchShippingMethods,
    fetchShippingOptions 
  } = props;
  const [bondFilename, setBondFileName] = useState("");
  const [isFetchingShipping, setIsFetchingShipping] = useState(false);

  useEffect(() => {
    const filename = `${
      bondRecord?.product?.name || bondRecord?.product_name || "bond"
    }`;
    const changedFileName = filename.replaceAll(" ", "_").toLowerCase();
    setBondFileName(changedFileName);
  }, [bondRecord]);

  if (!bondRecord) {
    return <div></div>;
  }

  const {
    fees_total,
    net_due_total,
    bond_premium_amount,
    status_name,
    salesforce_allow_ach_payment,
    salesforce_allow_cc_payment,
  } = bondRecord;

  const isPaymentButtonDisabled =
    !paymentLink?.length ||
    (!salesforce_allow_ach_payment && !salesforce_allow_cc_payment);

  const handleBuyNowClick = async () => {
    setIsFetchingShipping(true);
    try {
      // Fetch both shipping methods and options before proceeding
      if (fetchShippingMethods) {
        await fetchShippingMethods();
      }
      if (bondRecord?.is_shipping_required && fetchShippingOptions) {
        await fetchShippingOptions();
      }
      onSubmit();
    } catch (error) {
      console.error("Error fetching shipping data:", error);
    } finally {
      setIsFetchingShipping(false);
    }
  };

  return (
    <>
      {isLoading || isFetchingShipping ? (
        <StyledSpinnerContainer>
          <Spinner />
          <span>Loading...</span>
        </StyledSpinnerContainer>
      ) : status_name === "PAYMENT_REQUIRED" && !isPaymentButtonDisabled ? (
        <>
          {!!fees_total ? (
            <StyledPriceWrapper>
              <StyledPriceLabel>Bond Premium:</StyledPriceLabel>
              <StyledPriceValue>${bond_premium_amount.toFixed(2)}</StyledPriceValue>
              <StyledPriceLabel>Fees:</StyledPriceLabel>
              <StyledPriceValue>${fees_total.toFixed(2)}</StyledPriceValue>
              <StyledBorder />
              <StyledPriceLabel
                style={{ fontWeight: "bold", textAlign: "center" }}
              >
                Total Price:
              </StyledPriceLabel>
              <StyledPriceValue style={{ fontWeight: "bold" }}>
                ${net_due_total.toFixed(2)}
              </StyledPriceValue>
            </StyledPriceWrapper>
          ) : (
            <StyledPrice>Bond Premium: ${net_due_total.toFixed(2)}</StyledPrice>
          )}
          <StyledBondPreviewSection>
            {
              <>
                <StyledButtonsWrapper>
                  <StyledBondPreviewButton
                    disabled={isPaymentButtonDisabled || isLoading || isFetchingShipping}
                  >
                    <StyledBondPreview
                      isDisabled={isPaymentButtonDisabled || isLoading || isFetchingShipping}
                      onClick={handleBuyNowClick}
                    >
                      BUY NOW
                    </StyledBondPreview>
                  </StyledBondPreviewButton>
                  {pdfUrl && bondRecord?.is_bond_sample_pdf_available ? (
                    <StyledDownloadButton
                      disabled={isLoading || !pdfUrl}
                      onClick={() => downloadPDF(pdfUrl, bondFilename)}
                    >
                      Download Sample
                    </StyledDownloadButton>
                  ) : (
                    <></>
                  )}
                </StyledButtonsWrapper>
                {pdfUrl && bondRecord?.is_bond_sample_pdf_available && (
                  <object
                    width={"100%"}
                    style={{ height: 500 }}
                    data={pdfUrl}
                    type="application/pdf"
                  />
                )}
              </>
            }
          </StyledBondPreviewSection>
        </>
      ) : (
        <>
          <Notification isShowLink={true} status={status_name} />
        </>
      )}
    </>
  );
};

export default BondPreview;
